import React from 'react';
import PageIntro from '../components/PageIntro';
import { usePageMeta, SITE_NAME } from '../lib/usePageMeta';

const List = ({ items }) => (
  <ul className="mt-3 list-disc space-y-1.5 pl-5 marker:text-ink-soft">
    {items.map((item) => <li key={item}>{item}</li>)}
  </ul>
);

const sections = [
  {
    title: 'Information we collect',
    content: (
      <>
        <p>We may collect the following types of information.</p>
        <h3 className="mt-5 font-sans text-base font-semibold">Personal information</h3>
        <p className="mt-1">You may voluntarily provide personal information including:</p>
        <List items={['Name', 'Email address', 'Messages or inquiries submitted through contact forms', 'Feedback or comments']} />
        <h3 className="mt-5 font-sans text-base font-semibold">Non-personal information</h3>
        <p className="mt-1">We may automatically collect certain non-personal information, including:</p>
        <List items={['Browser type', 'Device information', 'IP address', 'Operating system', 'Pages visited', 'Time spent on pages', 'Referral source']} />
      </>
    ),
  },
  {
    title: 'How we use your information',
    content: (
      <List
        items={[
          'Providing educational and dictionary-related content',
          'Improving website functionality and user experience',
          'Responding to inquiries and support requests',
          'Monitoring website traffic and analytics',
          'Preventing fraudulent or unauthorized activity',
          'Sending updates or important notifications when necessary',
        ]}
      />
    ),
  },
  {
    title: 'Cookies and tracking technologies',
    content: (
      <>
        <p>Definitionofthe.com may use cookies and similar technologies to:</p>
        <List items={['Enhance browsing experience', 'Remember user preferences', 'Analyze website traffic', 'Improve content and services']} />
        <p className="mt-3">
          Users can choose to disable cookies through their browser settings. However, some features of the
          website may not function properly.
        </p>
      </>
    ),
  },
  {
    title: 'Third-party services',
    content: (
      <>
        <p>
          We may use trusted third-party services such as analytics providers, advertising partners, website
          hosting providers, and security and performance tools. These third parties may collect information in
          accordance with their own privacy policies.
        </p>
        <p className="mt-3">We are not responsible for the privacy practices of third-party websites linked from our platform.</p>
      </>
    ),
  },
  {
    title: 'Google AdSense and advertising',
    content: (
      <>
        <p>
          Our website may display advertisements provided by third-party advertising networks such as Google
          AdSense. These advertising partners may use cookies or similar technologies to deliver personalized ads
          based on users' browsing activities.
        </p>
        <p className="mt-3">
          Users can learn more about how Google uses information at{' '}
          <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-link">
            policies.google.com/technologies/ads
          </a>
          .
        </p>
      </>
    ),
  },
  {
    title: 'Data protection and security',
    content: (
      <p>
        We implement reasonable administrative, technical, and security measures to protect user information
        against unauthorized access, disclosure, alteration, or destruction. However, no method of online
        transmission or electronic storage is completely secure, and we cannot guarantee absolute security.
      </p>
    ),
  },
  {
    title: "Children's privacy",
    content: (
      <p>
        Definitionofthe.com is intended for educational purposes and accessible to users worldwide, including
        students and learners. We do not knowingly collect personal information from children under the age
        required by applicable law without parental consent. If you believe a child has provided personal
        information, please contact us immediately.
      </p>
    ),
  },
  {
    title: 'International users',
    content: (
      <p>
        As our services are available globally, your information may be processed and stored in countries
        outside your own jurisdiction. By using our website, you consent to such international data transfers
        where permitted by law.
      </p>
    ),
  },
  {
    title: 'Your privacy rights',
    content: (
      <>
        <p>Depending on your location and applicable laws, you may have rights including:</p>
        <List
          items={[
            'Access to your personal data',
            'Request correction of inaccurate information',
            'Request deletion of personal data',
            'Withdraw consent where applicable',
            'Object to certain data processing activities',
          ]}
        />
        <p className="mt-3">To exercise any of these rights, please contact us.</p>
      </>
    ),
  },
  {
    title: 'External links',
    content: (
      <p>
        Our website may contain links to external websites for educational or informational purposes. We are not
        responsible for the content, privacy policies, or practices of third-party websites.
      </p>
    ),
  },
  {
    title: 'Changes to this privacy policy',
    content: (
      <p>
        We reserve the right to update or modify this Privacy Policy at any time. Any changes will be posted on
        this page with an updated effective date. Users are encouraged to review this page periodically.
      </p>
    ),
  },
  {
    title: 'Contact us',
    content: (
      <>
        <p>If you have any questions, please contact us at:</p>
        <ul className="mt-3 space-y-1.5">
          <li><a href="mailto:gajendrajha7@gmail.com" className="text-link">gajendrajha7@gmail.com</a></li>
          <li><a href="mailto:megajendrajha@gmail.com" className="text-link">megajendrajha@gmail.com</a></li>
          <li><a href="https://definitionofthe.com" className="text-link">definitionofthe.com</a></li>
        </ul>
      </>
    ),
  },
];

const PrivacyPolicy = () => {
  usePageMeta(
    `Privacy Policy | ${SITE_NAME}`,
    'How Definitionofthe.com collects, uses and protects information, including cookies and Google AdSense advertising.'
  );

  return (
    <div className="wrap pt-12 md:pt-16">
      <PageIntro title="Privacy policy" note="Website: definitionofthe.com">
        We value the privacy of our visitors and are committed to protecting the personal information shared with
        us. By using this website, you agree to the practices described in this policy.
      </PageIntro>

      {/* Numbered: a legal document is read and cited by section */}
      <ol className="mt-12 max-w-2xl">
        {sections.map((section, index) => (
          <li key={section.title} className="border-t border-rule py-8">
            <h2 className="font-serif text-2xl font-semibold">
              <span className="mr-3 font-sans text-base font-semibold text-ink-soft">{index + 1}.</span>
              {section.title}
            </h2>
            <div className="mt-3 leading-relaxed">{section.content}</div>
          </li>
        ))}
      </ol>

      <p className="max-w-2xl border-t border-rule pt-8 font-serif text-lg">
        By using Definitionofthe.com, you consent to this Privacy Policy and agree to its terms.
      </p>
    </div>
  );
};

export default PrivacyPolicy;
